import { Spin } from 'antd';
import { AxiosResponse } from 'axios';
import { dateParamSignal } from 'CalendarFilter/DatePicker';
import Modal, { IModalProps } from 'components/Modal';
import { StaffByClockInOut } from 'components/Staff';
import Text from 'components/Text';
import { STAFF_AVAILABLE_PICKER_INDEX } from 'constants/variables';
import orderBy from 'lodash/orderBy';
import moment from 'utils/moment';
import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import shopActions from 'services/actions/shop';
import apis from 'services/apis';
import shopSelectors from 'services/selectors/shop';
import { IStaffResItem } from 'services/types/response';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { IStaffUI } from 'types/staff';
import InputSearch from 'widgets/Staff/InputSearch';
import { isBookableStaff } from 'utils/staff';
type Props = {
  staffId?: string;
  onChoose?: (o: IStaffUI) => void;
  onChooseAll?: () => void;
  renderHeader?: IModalProps['renderHeader'];
  zIndex?: number;
  title?: string;
  newTitle?: boolean;
};
type StaffAvailablePickerRef = {
  get: (filterIds?: string[] | undefined) => Promise<IStaffUI>;
  getAvailableWithTime: (query: { startTime: string, endTime: string }, filterIds?: string[] | undefined) => Promise<IStaffUI>;
};
export const staffAvailablePickerRef = React.createRef<StaffAvailablePickerRef>();
export const useStaffAvailablePickerRef = () => useRef<StaffAvailablePickerRef>(null);
const StaffAvailablePickerUI = forwardRef<StaffAvailablePickerRef, Props>(({ onChoose = () => undefined, renderHeader, zIndex, title, newTitle }, ref) => {
  const dispatch = useAppDispatch();
  const _staffsStore = shopSelectors.staffsAvailable();
  const [open, setOpen] = useState(false);
  const resolveCallback = useRef<(o: IStaffUI) => void>(() => undefined);
  const clockInStaffs = shopSelectors.clockInStaffs();
  const [filterIds, setFilterIds] = useState<string[] | undefined>(undefined);
  const [isAvailableWithTime, setIsAvailableWithTime] = useState(false);
  const [staffsAvailable, setStaffsAvailable] = useState<IStaffUI[]>([]);
  const [loading, setLoading] = useState(false);

  // Both paths feed the same picker, so Maintenance is dropped once here:
  // `staffsAvailable` comes from the by-time endpoint, `_staffsStore` from the
  // booking list. `clockInStaffs` below is only a lookup used to decorate these,
  // so it needs no filter of its own.
  const _staffs = useMemo(() => {
    return (isAvailableWithTime ? staffsAvailable : _staffsStore).filter(
      isBookableStaff,
    );
  }, [isAvailableWithTime, staffsAvailable, _staffsStore]);

  const staffs = useMemo(() => {
    const anyOne = _staffs.find(o => o.id?.toString() === '0');
    const staffListData = filterIds?.length ? _staffs.filter(o => o.id?.toString() !== '0' && !filterIds.includes(o.id?.toString() || '')) : _staffs.filter(o => o.id?.toString() !== '0');
    const staffList = staffListData.map(o => {
      const clockInStaff = clockInStaffs?.find(s => s.id?.toString() === o.id?.toString());
      return ({
        ...o,
        isLockedin: clockInStaff?.isLockedin,
        indexClockInStaff: clockInStaff?.indexClockInStaff,
        strClockInTime: clockInStaff?.strClockInTime,
      }) as IStaffUI;
    });
    const orderList = orderBy(
      staffList,
      [
        (s: IStaffUI) => !!s.isLockedin,
        (s: IStaffUI) => Number(s.indexClockInStaff) || 0,
        (s: IStaffUI) => (s.name || '').toLowerCase()
      ],
      ['desc', 'asc', 'asc']
    );
    return anyOne ? [anyOne, ...orderList] : orderList;
  }, [clockInStaffs, _staffs, isAvailableWithTime, filterIds, staffsAvailable]);

  const handleChoose = (o: IStaffUI) => () => {
    onChoose(o);
    resolveCallback.current(o);
    setOpen(false);
  };

  const handleGet = async (_filterIds?: string[] | undefined) => {
    setFilterIds(_filterIds || undefined);
    setIsAvailableWithTime(false);
    setOpen(true);
    if (dateParamSignal.value?.format('MM-DD-YYYY') === moment().format('MM-DD-YYYY')) {
      dispatch(shopActions.clockInStaffs.fetch());
    } else {
      dispatch(shopActions.clockInStaffs.fetchPaging());
    }
    return new Promise<IStaffUI>((resolve) => {
      resolveCallback.current = resolve;
    });
  };

  const handleGetAvailableWithTime = async (query: { startTime: string, endTime: string }, _filterIds?: string[] | undefined) => {
    setFilterIds(_filterIds || undefined);
    setIsAvailableWithTime(true);
    setOpen(true);
    setLoading(true);
    try {
      const res: AxiosResponse<{ payload: IStaffResItem[] }> = await apis.getStaffAvailableBooking(query);
      if (res.data.payload) {
        const data: IStaffUI[] = res.data.payload.map((o) => ({
          avatar: o.urlImage,
          extraData: o,
          id: o.id?.toString(),
          name: o.nickName || o.firstName,
          timeSchedule: o.timeSchedule,
        }));
        setStaffsAvailable(data);
      }
    } catch (error) {
      setStaffsAvailable([]);
    } finally {
      setLoading(false);
    }
    return new Promise<IStaffUI>((resolve) => {
      resolveCallback.current = resolve;
    });
  };

  useImperativeHandle(ref, () => ({
    get: handleGet,
    getAvailableWithTime: handleGetAvailableWithTime
  }));

  return (
    <>
      <Modal
        open={open}
        title={newTitle ? undefined : (title || 'BOOK BY STAFF')}
        visibleOk={false}
        renderHeader={renderHeader}
        width='95vw'
        closeRight
        zIndex={zIndex || STAFF_AVAILABLE_PICKER_INDEX}
        onCancel={() => setOpen(false)}>
        <Spin spinning={loading}>
          {!!newTitle && <Text variant='LABEL_SECTION' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', textAlign: 'center', fontSize: 24, marginBottom: '-8px' }}>Book By Staff</Text>}
          <StaffTabStyled>
            <InputSearch staffs={staffs} onSelect={(o) => handleChoose(o)()} />
            <Staffs >
              {
                staffs.map(o => (
                  <StaffByClockInOut key={o.id} data={o} onClick={handleChoose(o)} />
                ))
              }
            </Staffs>
          </StaffTabStyled>
        </Spin>
      </Modal>
    </>
  );
});
StaffAvailablePickerUI.displayName = 'StaffAvailablePickerUI';
export { StaffAvailablePickerUI };

const StaffAvailablePicker = {
  get: (filterIds?: string[] | undefined) => staffAvailablePickerRef.current?.get(filterIds),
  getAvailableWithTime: (query: { startTime: string, endTime: string }, filterIds?: string[] | undefined) => staffAvailablePickerRef.current?.getAvailableWithTime(query, filterIds),
};

export default StaffAvailablePicker;

const StaffTabStyled = styled.div`
  height: 100%;
  padding: 1.5rem;
  flex-grow: 1;
`;

const Staffs = styled.div`
display: grid;
grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
gap: 8px;
margin-top: 12px;
flex-grow: 1;
zoom: 0.9;
`;

